import type { AxiosRequestConfig } from 'axios';

import { axios, organizationIdConfig } from 'configs';
import type { ELineChannelStatus } from 'types';

export type ScenarioState = 'draft' | 'active' | 'paused' | 'archived';
export type ScenarioTriggerType = 'friend_add' | 'tag_added' | 'manual';
type TerminalEnrollmentState = 'completed' | 'cancelled' | 'failed';
export type ScenarioEnrollmentState = 'active' | TerminalEnrollmentState;
type JsonPrimitive = string | number | boolean | null;
export type ScenarioJsonValue = JsonPrimitive | ScenarioJsonValue[] | { [key: string]: ScenarioJsonValue };
type Empty = Record<never, never>;
type Num<K extends string> = Record<K, number>;
type Str<K extends string> = Record<K, string>;
type Maybe<K extends string, Value> = Record<K, Value | null>;
type No<K extends string> = { [P in K]?: never };
type Tag<T extends string, F extends object = Empty> = { type: T } & F;
type Id<F extends object> = { id: string } & F;
type Mode<M extends string, F extends object> = { mode: M } & F;
export type ScenarioTriggerV1 = Tag<'friend_add'> | Tag<'tag_added', { tagId: number | null }> | Tag<'manual'>;
export type ScenarioStepTimingV1 = Mode<'relative', Maybe<'delayMinutes', number>> | Mode<'elapsed', Maybe<'elapsedMinutes', number>> | Mode<'absolute_time', Maybe<'dayOffset' | 'minuteOfDay', number>>;
export type ScenarioConditionMatchV1 = 'tag_all' | 'tag_any' | 'tag_none';
export type ScenarioConditionV1 = Tag<ScenarioConditionMatchV1, { tagIds: number[] }>;
export type ScenarioImageMediaV1 = Maybe<'type' | 'url' | 'fileName', string> & Maybe<'originalWidth' | 'originalHeight', number> & Maybe<'actionUrl' | 'actionLabel', string>;
export type ScenarioVideoMediaV1 = ScenarioImageMediaV1 & Maybe<'poster', string>;
export type ScenarioMessageV1 =
  | Id<Tag<'text', { content: string | null }>>
  | Id<Tag<'image', { media: ScenarioImageMediaV1 | null }>>
  | Id<Tag<'video', { media: ScenarioVideoMediaV1 | null }>>
  | Id<Tag<'richMessage', { assetId: number | null }>>
  | Id<Tag<'cardTypeMessage', { assetId: number | null }>>;
export type ScenarioStepV1 = Id<{
  timing: ScenarioStepTimingV1 | null;
  messages: ScenarioMessageV1[];
  condition: ScenarioConditionV1 | null;
}> &
  Maybe<'falseTargetStepId', string> &
  Maybe<'onReachTagId', number>;
export type ScenarioDefinitionV1 = {
  schemaVersion: 1;
  trigger: ScenarioTriggerV1 | null;
  steps: ScenarioStepV1[];
};
type ScenarioCommon = Num<'id'> &
  Str<'name' | 'createdAt' | 'updatedAt'> & {
    state: ScenarioState;
    hasUnpublishedChanges: boolean;
  };
type Page<Item> = { items: Item[]; nextCursor: string | null };
type Ver = { expectedEditVersion: number };
/** 一覧のfilter対象アカウント、および編集画面が停止理由を出すための状態つきアカウント。 */
export type ScenarioLineChannel = Num<'id'> & Str<'basicId'> & {
  memo: string | null;
  status: ELineChannelStatus;
};
/** 一覧itemの対象アカウント。statusは含まれない。 */
export type ScenarioTargetChannel = Num<'id'> & Str<'basicId'> & { memo: string | null };
/**
 * 対象アカウント。作成時に決まり、以後変更されない。
 * targetsAllAccounts=trueならlineChannelsは空配列、falseならID昇順の対象アカウント。
 */
type ScenarioTargets<Channel> = {
  targetsAllAccounts: boolean;
  lineChannels: Channel[];
};
export type ScenarioListItem = ScenarioCommon &
  ScenarioTargets<ScenarioTargetChannel> & {
    publishedTrigger: ScenarioTriggerType | null;
    currentPublishNumber: number | null;
  } & Num<'stepCount' | 'resumableFailedRunCount'>;
export type ScenarioListRequest = {
  lineChannelId?: number;
  state?: ScenarioState;
  cursor?: string;
};
export type ScenarioListResponse = Page<ScenarioListItem> & Maybe<'lineChannel', ScenarioLineChannel>;
export type ScenarioRecord = ScenarioCommon &
  Num<'organizationId' | 'editVersion'> &
  Maybe<'triggerTagId' | 'currentRevisionId', number> & {
    description: string | null;
    trigger: ScenarioTriggerType | null;
    editingDefinition: ScenarioDefinitionV1;
  };
export type CreateScenarioRequest = {
  /** 省略で組織の全アカウント対象。配列指定でそのアカウントだけを対象にする（1件以上・重複なし・空配列は400） */
  lineChannelIds?: number[];
  name: string;
  description?: string | null;
};
type ScenarioEditableFields = {
  name: string;
  description: string | null;
  definition: ScenarioDefinitionV1;
};
type AtLeastOne<T> = {
  [K in keyof T]: Pick<T, K> & Partial<Omit<T, K>>;
}[keyof T];
export type UpdateScenarioRequest = Ver & AtLeastOne<ScenarioEditableFields>;
export type ScenarioRevisionSummary = Num<'revisionId' | 'publishNumber'> & Str<'publishedAt'>;
type AssetReference = Num<'assetId'> & {
  messageType: 'richMessage' | 'cardTypeMessage';
};
export type ScenarioAssetReference = AssetReference & ({ status: 'available'; name: string } | { status: 'deleted' });
export type ScenarioPublishExpectedImpact = Num<`${'active' | 'switchable' | 'blockedCurrentStep' | 'completing'}RunCount` | 'skippedUnprocessedStepCount'> & {
  nextStepCounts: Array<Str<'stepId'> & Num<'stepOrder' | 'runCount'>>;
};
export type ScenarioPublishImpact = ScenarioPublishExpectedImpact & {
  dependencyProjectionMutationLimitExceeded: boolean;
  canApplyToActiveRuns: boolean;
};
export type ScenarioDetail = ScenarioCommon &
  ScenarioTargets<ScenarioLineChannel> &
  Num<'editVersion'> &
  Maybe<'currentRevisionId', number> & {
    description: string | null;
    definitionSource: 'editing' | 'revision';
    definition: ScenarioDefinitionV1;
    currentRevisionSummary: ScenarioRevisionSummary | null;
    selectedRevisionSummary: ScenarioRevisionSummary | null;
    publishPreview: ScenarioPublishImpact | null;
    assetReferences: ScenarioAssetReference[];
  };
export type ScenarioDetailRequest = (Num<'revisionId'> & No<'includePublishPreview' | 'revisionCursor'>) | (No<'revisionId' | 'revisionCursor'> & { includePublishPreview?: true });
export type ScenarioRevisionPageRequest = Str<'revisionCursor'> & No<'revisionId' | 'includePublishPreview'>;
type Revisions<Detail> = {
  detail: Detail;
  revisionSummaries: ScenarioRevisionSummary[];
  nextRevisionCursor: string | null;
};
export type ScenarioDetailResponse = Revisions<ScenarioDetail>;
export type ScenarioRevisionPageResponse = Revisions<null>;
type Apply<B extends boolean, F extends object> = { applyToActiveRuns: B } & F;
export type PublishScenarioRequest = Ver & (Apply<false, No<'expectedImpact'>> | Apply<true, { expectedImpact: ScenarioPublishExpectedImpact }>);
export type PublishScenarioResponse = {
  scenario: ScenarioRecord;
  revision: ScenarioRevisionSummary;
  impact: ScenarioPublishImpact | null;
};
export type ScenarioAction = 'pause' | 'resume' | 'archive' | 'restore';
type StateChange = { action: ScenarioAction; expectedState: ScenarioState };
export type ChangeScenarioStateRequest = StateChange;

type Target<M extends string, T> = Mode<M, { target: T }>;
type Outcome<S extends string, F extends object = Empty> = Num<'friendId'> & {
  status: S;
} & F;
type FriendsTarget = Tag<'friends', { friendIds: number[] }>;
export type ScenarioManualFriendsTarget = FriendsTarget;
export type ScenarioManualBatchTarget = { type: 'tag'; tagId: number } | { type: 'all' };
type ExpectedManualCounts = Num<`expected${'Target' | 'Startable'}Count`>;
type ManualRequests = {
  preview: Target<'preview', ScenarioManualBatchTarget>;
  friends: Target<'commit', ScenarioManualFriendsTarget>;
  batch: Target<'commit', ScenarioManualBatchTarget> & ExpectedManualCounts;
};
export type ScenarioManualPreviewRequest = ManualRequests['preview'];
export type ScenarioManualFriendsCommitRequest = ManualRequests['friends'];
export type ScenarioManualBatchCommitRequest = ManualRequests['batch'];
export type ScenarioManualRequest = ManualRequests[keyof ManualRequests];
type PreviewCounts = Num<`${'target' | 'startable' | 'skip'}Count`>;
export type ScenarioManualPreviewResponse = PreviewCounts;
export type ScenarioManualSkipReason = 'NOT_FOLLOWING' | 'ACTIVE_RUN_EXISTS';
export type ScenarioManualOutcome = Outcome<'started'> | Outcome<'skipped', { reason: ScenarioManualSkipReason }>;
export type ScenarioManualCommitResponse = Num<'targetCount' | 'startedCount' | 'skippedCount'> & {
  skippedByReason: Record<ScenarioManualSkipReason, number>;
  outcomes?: never;
};
export type ScenarioManualFriendsCommitResponse = Omit<ScenarioManualCommitResponse, 'outcomes'> & { outcomes: ScenarioManualOutcome[] };
type TestSendRequests = {
  prepare: Mode<'prepare', Ver & Str<'stepId'> & { friendIds: number[] }>;
  commit: Mode<'commit', { testSendToken: string }>;
};
export type ScenarioTestSendPrepareRequest = TestSendRequests['prepare'];
export type ScenarioTestSendCommitRequest = TestSendRequests['commit'];
export type ScenarioTestSendRequest = TestSendRequests[keyof TestSendRequests];
export type ScenarioTestSendPrepareResponse = Str<'testSendToken' | 'expiresAt' | 'stepId'> & {
  recipients: Array<{
    friendId: number;
    messages: Array<Record<string, ScenarioJsonValue>>;
  }>;
};
export type ScenarioTestSendNotSentReason = 'NOT_FOLLOWING' | 'NOT_TEST_USER' | 'RECIPIENT_UNAVAILABLE' | 'SCENARIO_ARCHIVED' | 'ACCOUNT_UNAVAILABLE' | 'DISPATCH_LEASE_LOST' | 'OPERATION_DEADLINE_EXCEEDED' | 'TEMPORARILY_UNAVAILABLE';
export type ScenarioTestSendOutcome = Outcome<'sent'> | Outcome<'failed' | 'unknown', { reason?: string }> | Outcome<'not_sent', { reason: ScenarioTestSendNotSentReason }>;
export type ScenarioTestSendCommitResponse = Num<`${'total' | 'sent' | 'failed' | 'unknown' | 'notSent'}Count`> & {
  outcomes: ScenarioTestSendOutcome[];
};
type DeliveryMetric = 'sent' | 'failed' | 'processing';
export type ScenarioStatsStep = Str<'stepId'> & Num<'stepOrder' | DeliveryMetric | `cancelled${'Before' | 'After'}Attempt` | `${'target' | 'reached'}Count`> & Maybe<'reachRate' | 'successRate', number>;
type ScenarioStatsSummaryBase = Num<'runCount'> & {
  statusCounts: Record<ScenarioEnrollmentState, number>;
};
// 共有リンク単位のクリック数。置換できなかったURLはclickCount=nullで未計測を表す
// uniqueClickCountは`?r=`を解決できたRunの実数。clickRateはユニーク÷送信成功Run数で分母0はnull
export type ScenarioStatsUrlClick = Str<'stepId' | 'displayUrl'> & {
  clickCount: number | null;
  uniqueClickCount: number | null;
  clickRate: number | null;
};
export type ScenarioStatsSummaryResponse = ScenarioStatsSummaryBase & ({ revisionId?: never; steps: null; urlClicks?: never } | { revisionId: number; steps: ScenarioStatsStep[]; urlClicks: ScenarioStatsUrlClick[] });
export type ScenarioFailureDeliveryStatus = 'failed' | 'pending' | 'sending' | 'retry_wait';
export type ScenarioFailure = Num<`${'delivery' | 'enrollment' | 'friend' | 'revision'}Id` | 'attemptCount' | 'publishNumber' | 'stepOrder'> &
  Str<'stepId' | 'friendName'> &
  Maybe<`${'failed' | 'firstAttempt' | 'retryExpires'}At` | `failure${'Type' | 'Reason'}`, string> & { deliveryStatus: ScenarioFailureDeliveryStatus; httpStatus: number | null; retrying: boolean; resumable: boolean };
export type ScenarioFailuresResponse = Page<ScenarioFailure>;
type StatsQuery<View extends string, Cursor> = {
  view: View;
  revisionId?: number;
  cursor?: Cursor;
};
export type ScenarioStatsSummaryRequest = StatsQuery<'summary', never>;
export type ScenarioFailuresRequest = StatsQuery<'failures', string>;
export type ScenarioStatsRequest = ScenarioStatsSummaryRequest | ScenarioFailuresRequest;
export type ScenarioResumeResponse = {
  status: 'sent' | 'failed' | 'unknown' | 'not_sent' | 'stale' | 'cancelled';
  retrying?: boolean;
};
type ManualResponse = ScenarioManualPreviewResponse | ScenarioManualCommitResponse | ScenarioManualFriendsCommitResponse;
type TestSendResponse = ScenarioTestSendPrepareResponse | ScenarioTestSendCommitResponse;
type StatsResponse = ScenarioStatsSummaryResponse | ScenarioFailuresResponse;
const unwrap = async <T>(request: PromiseLike<{ data: T }>): Promise<T> => (await request).data;
const scenarioUrl = (scenarioId: number, suffix = '') => `/scenarios/${scenarioId}${suffix}`;
const getData = async <T>(url: string, params: object, organizationId?: number | null) => unwrap(axios.get<T>(url, { params, ...organizationIdConfig(organizationId) }));
const patchData = async <T>(url: string, body: object) => unwrap(axios.patch<T>(url, body));
const postData = async <T>(url: string, body?: object, config?: AxiosRequestConfig) => unwrap(body === undefined ? axios.post<T>(url) : config === undefined ? axios.post<T>(url, body) : axios.post<T>(url, body, config));

export const listScenarios = (request: ScenarioListRequest) => getData<ScenarioListResponse>('/scenarios', request);
export const createScenario = (request: CreateScenarioRequest) => postData<ScenarioRecord>('/scenarios', request);
type RReq = ScenarioRevisionPageRequest;
type RRes = ScenarioRevisionPageResponse;
type DReq = ScenarioDetailRequest;
type DRes = ScenarioDetailResponse;
export function getScenario(id: number, request: RReq, organizationId?: number | null): Promise<RRes>;
export function getScenario(id: number, request?: DReq, organizationId?: number | null): Promise<DRes>;
export function getScenario(id: number, request: DReq | RReq = {}, organizationId?: number | null) {
  return getData<DRes | RRes>(scenarioUrl(id), request, organizationId);
}
export const updateScenario = (scenarioId: number, request: UpdateScenarioRequest) => patchData<ScenarioRecord>(scenarioUrl(scenarioId), request);
export const publishScenario = (scenarioId: number, request: PublishScenarioRequest) => postData<PublishScenarioResponse>(scenarioUrl(scenarioId, '/publish'), request);
export const changeScenarioState = (scenarioId: number, request: ChangeScenarioStateRequest) => postData<ScenarioRecord>(scenarioUrl(scenarioId, '/actions'), request);
export function manualEnrollScenario(scenarioId: number, request: ScenarioManualPreviewRequest): Promise<ScenarioManualPreviewResponse>;
export function manualEnrollScenario(scenarioId: number, request: ScenarioManualFriendsCommitRequest, idempotencyKey: string): Promise<ScenarioManualFriendsCommitResponse>;
export function manualEnrollScenario(scenarioId: number, request: ScenarioManualBatchCommitRequest, idempotencyKey: string): Promise<ScenarioManualCommitResponse>;
export async function manualEnrollScenario(scenarioId: number, request: ScenarioManualRequest, idempotencyKey?: string) {
  return postData<ManualResponse>(scenarioUrl(scenarioId, '/manual-enrollments'), request, {
    ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
  });
}
export function testSendScenario(scenarioId: number, request: ScenarioTestSendPrepareRequest, organizationId?: number | null): Promise<ScenarioTestSendPrepareResponse>;
export function testSendScenario(scenarioId: number, request: ScenarioTestSendCommitRequest, organizationId?: number | null): Promise<ScenarioTestSendCommitResponse>;
export async function testSendScenario(scenarioId: number, request: ScenarioTestSendRequest, organizationId?: number | null) {
  return postData<TestSendResponse>(scenarioUrl(scenarioId, '/test-send'), request, organizationIdConfig(organizationId));
}
export function getScenarioStats(scenarioId: number, request: ScenarioStatsSummaryRequest): Promise<ScenarioStatsSummaryResponse>;
export function getScenarioStats(scenarioId: number, request: ScenarioFailuresRequest): Promise<ScenarioFailuresResponse>;
export async function getScenarioStats(scenarioId: number, request: ScenarioStatsRequest) {
  return getData<StatsResponse>(scenarioUrl(scenarioId, '/stats'), request);
}
export const resumeScenarioEnrollment = (scenarioId: number, enrollmentId: number) => postData<ScenarioResumeResponse>(scenarioUrl(scenarioId, `/enrollments/${enrollmentId}/resume`));
